import React, { useState } from 'react';
import Navigation from './Navigation';
import UTCTimer from './UTCTimer';
import TwitterCreditDisplay from './TwitterCreditDisplay';
import { useUIStore } from '../stores/ui/uiStore';
import { useToasterStore } from '../stores/ui/toasterStore';
import { useDataStore } from '../stores/data/dataStore';
import FilterIcon from './FilterIcon';
import GlobalDeckFilterModal from './GlobalDeckFilterModal';
import SettingsModal from './SettingsModal';
import {
  allCategories,
  allStatuses,
  isGlobalFilterActive,
  useGlobalDeckFilterStore,
} from '../stores/ui/globalDeckFilterStore';
import { TokenCategory, TokenStatus, TOKEN_STATUS_LABELS } from '../stores/data/types/feTokenState';
import type { TokenState } from '../api/fe-management-system-api/types';
import { retrospectiveApi } from '../api/fe-management-system-api/retrospective';

interface HeaderProps {
  title: string;
  showPageControls?: boolean;
}

const Header: React.FC<HeaderProps> = ({ title, showPageControls = false }) => {
  const { 
    searchQuery,
    setSearchQuery, 
    setSearchResults, 
    setIsSearchMode,
    isSearchMode,
    maxCardCount, 
    setMaxCardCount,
    getTotalTokenCount
  } = useUIStore();
  const { showToaster } = useToasterStore();
  const { tokenStateMap } = useDataStore();
  const [inputValue, setInputValue] = useState(searchQuery);
  const [isGlobalFilterOpen, setIsGlobalFilterOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isRetrospectLoading, setIsRetrospectLoading] = useState(false);
  const {
    selectedCategories,
    selectedStatuses,
    setCategories,
    setStatuses,
    setRetrospectiveTokens,
  } = useGlobalDeckFilterStore();
  const globalFilterActive = isGlobalFilterActive(selectedCategories, selectedStatuses);

  const handleSearch = () => {
    const trimmedQuery = inputValue.trim();
    
    if (!trimmedQuery) {
      setSearchQuery('');
      setSearchResults(null);
      setIsSearchMode(false);
      return;
    }

    // 프론트엔드 tokenStateMap에서 검색
    const keyword = trimmedQuery.toLowerCase();
    const matchingAddresses: string[] = [];

    Object.values(tokenStateMap).forEach((tokenState) => {
      const address = tokenState.tokenAddress?.toLowerCase() || '';
      const symbol = tokenState.tokenInfo?.tokenSymbol?.toLowerCase() || '';
      const name = tokenState.tokenInfo?.tokenName?.toLowerCase() || '';
      const memo = tokenState.feTokenState?.memo?.toLowerCase() || '';

      if (
        address.includes(keyword) ||
        symbol.includes(keyword) ||
        name.includes(keyword) ||
        memo.includes(keyword)
      ) {
        matchingAddresses.push(tokenState.tokenAddress);
      }
    });

    setSearchQuery(trimmedQuery);
    setSearchResults(matchingAddresses.length > 0 ? matchingAddresses : null);
    setIsSearchMode(true);
    
    if (matchingAddresses.length === 0) {
      showToaster('No search results.', 'info');
    } else {
      showToaster(`Found ${matchingAddresses.length} token(s).`, 'success');
    }
  };

  const handleClearSearch = () => {
    setInputValue('');
    setSearchQuery('');
    setSearchResults(null);
    setIsSearchMode(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const applyPreset = (categories: TokenCategory[], statuses: TokenStatus[]) => {
    setCategories(categories);
    setStatuses(statuses);
  };

  const handlePresetAll = () => {
    applyPreset(allCategories, allStatuses);
    setRetrospectiveTokens({});

  };

  const handlePresetReview = () => {
    applyPreset(allCategories, [TokenStatus.REVIEWING, TokenStatus.FETCHING_DATA, TokenStatus.FETCHING_FAILED]);
    setRetrospectiveTokens({});
  };

  const handlePresetEmpty = () => {
    applyPreset(allCategories, [TokenStatus.NONE]);
    setRetrospectiveTokens({});

  };

  const handlePresetIC = () => {
    applyPreset(allCategories, [TokenStatus.BUY, TokenStatus.READY_FOR_IC]);
    setRetrospectiveTokens({});
  };

  const handlePresetMonitoring = () => {
    applyPreset(allCategories, [TokenStatus.BUY, TokenStatus.HOLD]);
    setRetrospectiveTokens({});
  };

  const handlePresetReset = () => {
    applyPreset(allCategories, []);
    setRetrospectiveTokens({});
  };

  const handleRetrospect = async () => {
    applyPreset(allCategories, allStatuses);
    setIsRetrospectLoading(true);
    try {
      const tokens = await retrospectiveApi.fetchRetrospectiveTokens('false');
      const tokenMap: Record<string, TokenState> = {};
      tokens.forEach((token: TokenState) => {
        if (token.tokenAddress) {
          tokenMap[token.tokenAddress] = token;
        }
      });
      setRetrospectiveTokens(tokenMap);
      console.log(tokenMap);
      showToaster(`Loaded ${tokens.length} retrospective token(s).`, 'success');
    } catch (error: any) {
      showToaster(
        error?.message || 'Failed to load Retrospect tokens.',
        'error',
      );
    } finally {
      setIsRetrospectLoading(false);
    }
  };

  const toggleStatus = (status: TokenStatus) => {
    if (selectedStatuses.includes(status)) {
      // 최소 하나는 선택되어 있어야 하거나, 그냥 필터링
      setStatuses(selectedStatuses.filter((s) => s !== status));
    } else {
      setStatuses([...selectedStatuses, status]);
    }
    setRetrospectiveTokens({});
  };

  return (
    <header className="bg-gray-800 border-b border-gray-700 px-10 py-2">
      <div className="max-w-8xl mx-auto flex justify-between items-center">
        <div className="flex items-center space-x-8">
          <Navigation />
          {showPageControls && (
            <div className="flex items-center space-x-2 pl-[34px] gap-20">
              <div className="relative flex items-center gap-2">
                {isSearchMode ? (
                  <div className="px-3 py-0.5 text-sm bg-gray-900 border border-gray-700 rounded-md text-gray-300 w-64 pr-8 flex items-center">
                    <span className="truncate">{searchQuery}</span>
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder="Token Address, Symbol, Name..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="px-3 py-0.5 text-sm bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 w-64 pr-8"
                  />
                )}
                {(isSearchMode) && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {!isSearchMode && (
                  <button
                    onClick={handleSearch}
                    className="px-3 py-1 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                )}
              </div>
             
              <div className="flex items-center space-x-2">
                <label className="text-xs text-gray-400">Limit:</label>
                <select
                  value={maxCardCount}
                  onChange={(e) => setMaxCardCount(Number(e.target.value))}
                  className="px-2 py-1 text-xs bg-gray-700 text-white border border-gray-600 rounded focus:outline-none focus:border-amber-700 transition-colors"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={150}>150</option>
                  <option value={200}>200</option>
                  <option value={250}>250</option>
                  <option value={300}>300</option>
                  <option value={350}>350</option>
                  <option value={400}>400</option>
                  <option value={450}>450</option>
                  <option value={500}>500</option>
                </select>
                {showPageControls && (
                  <span className="text-xs text-gray-400 ml-2">
                    Total: {getTotalTokenCount()}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePresetAll}
                className="rounded-full border border-gray-600 bg-gray-800 px-3 py-1 text-xs font-medium text-gray-100 transition-colors hover:border-white hover:text-white"
              >
                All
              </button>
              <button
                type="button"
                onClick={handlePresetEmpty}
                className="rounded-full border border-gray-600 bg-gray-800 px-3 py-1 text-xs font-medium text-gray-100 transition-colors hover:border-white hover:text-white"
              >
                Empty
              </button>
              <button
                type="button"
                onClick={handlePresetReview}
                className="rounded-full border border-gray-600 bg-gray-800 px-3 py-1 text-xs font-medium text-gray-100 transition-colors hover:border-white hover:text-white"
              >
                Review
              </button>
              <button
                type="button"
                onClick={handlePresetIC}
                className="rounded-full border border-gray-600 bg-gray-800 px-3 py-1 text-xs font-medium text-gray-100 transition-colors hover:border-white hover:text-white"
              >
                IC
              </button>
              <button
                type="button"
                onClick={handlePresetMonitoring}
                className="rounded-full border border-gray-600 bg-gray-800 px-3 py-1 text-xs font-medium text-gray-100 transition-colors hover:border-white hover:text-white"
              >
                Monitoring
              </button>
              <button
                type="button"
                onClick={handlePresetReset}
                className="rounded-full border border-gray-600 bg-gray-800 px-3 py-1 text-xs font-medium text-gray-100 transition-colors hover:border-white hover:text-white"
              >
                Reset
              </button>
              <div className="text-gray-400 text-xs mx-1">
                |
              </div>
              <button
                type="button"
                onClick={handleRetrospect}
                disabled={isRetrospectLoading}
                className={`rounded-full border border-gray-600 bg-gray-800 px-3 py-1 text-xs font-medium text-gray-100 transition-colors ${
                  isRetrospectLoading
                    ? 'border-gray-500 text-gray-400 cursor-wait bg-gray-700'
                    : 'hover:border-white hover:text-white'
                }`}
              >
                {isRetrospectLoading ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="h-2 w-2 animate-spin rounded-full border border-current border-t-transparent" />
                    Loading...
                  </span>
                ) : (
                  'Retrospect'
                )}
              </button>
            </div>
            
            {/* 세부 Status 토글 버튼 */}
            <div className="flex items-center gap-1.5">
              {Object.values(TokenStatus).map((status) => {
                const label = status === TokenStatus.NONE ? 'Empty' : TOKEN_STATUS_LABELS[status];
                const isActive = selectedStatuses.includes(status);
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => toggleStatus(status)}
                    className={` border px-2 py-0.5 text-[10px] font-medium transition-colors ${
                      isActive
                        ? 'border-blue-500 bg-blue-600/20 text-blue-400'
                        : 'border-gray-600 bg-gray-800 text-gray-400 hover:border-gray-500 hover:text-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          <TwitterCreditDisplay />
          <UTCTimer />
          <FilterIcon
            isActive={globalFilterActive}
            onClick={() => setIsGlobalFilterOpen(true)}
            title="전역 덱 필터"
          />
          <button
            onClick={() => setIsSettingsModalOpen(true)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            title="설정"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      </div>
      <GlobalDeckFilterModal
        isOpen={isGlobalFilterOpen}
        onClose={() => setIsGlobalFilterOpen(false)}
      />
      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />
    </header>
  );
};

export default Header; 